import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Copy, Users, Link2, Check } from "lucide-react";
import Layout from "../../components/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import { useConfirm } from "../../hooks/useConfirm";
import { vendedoresAPI } from "../../services/api";

const INSTITUCIONAL_URL = "https://nomoz.tech";

const AdminVendedores = () => {
  const { darkMode } = useTheme();
  const showConfirm = useConfirm();

  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    codigo: "",
    ativo: true,
    comissao_pct_ciclo1: 0.12,
    comissao_pct_demais: 0.04,
    observacoes: "",
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await vendedoresAPI.list();
      setVendedores(res.data || []);
    } catch (err) {
      toast.error("Erro ao carregar vendedores");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({
      nome: "",
      email: "",
      telefone: "",
      codigo: "",
      ativo: true,
      comissao_pct_ciclo1: 0.12,
      comissao_pct_demais: 0.04,
      observacoes: "",
    });
    setShowModal(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setFormData({
      nome: v.nome || "",
      email: v.email || "",
      telefone: v.telefone || "",
      codigo: v.codigo || "",
      ativo: v.ativo ?? true,
      comissao_pct_ciclo1: Number(v.comissao_pct_ciclo1 ?? 0.12),
      comissao_pct_demais: Number(v.comissao_pct_demais ?? 0.04),
      observacoes: v.observacoes || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.codigo) delete payload.codigo; // backend gera a partir do nome
      if (editing) {
        await vendedoresAPI.update(editing.id, payload);
        toast.success("Vendedor atualizado");
      } else {
        await vendedoresAPI.create(payload);
        toast.success("Vendedor criado");
      }
      setShowModal(false);
      await load();
    } catch (err) {
      const msg = err.response?.data?.message || "Erro ao salvar vendedor";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    }
  };

  const handleDelete = async (v) => {
    const ok = await showConfirm({
      title: "Desativar vendedor?",
      message: `O vendedor "${v.nome}" será desativado (soft delete). Leads já atribuídos continuam vinculados.`,
      confirmText: "Desativar",
    });
    if (!ok) return;
    try {
      await vendedoresAPI.remove(v.id);
      toast.success("Vendedor desativado");
      await load();
    } catch {
      toast.error("Erro ao desativar");
    }
  };

  const buildLink = (code) => `${INSTITUCIONAL_URL}/?v=${encodeURIComponent(code)}`;

  const copyLink = async (v, type = 'short') => {
    const code = type === 'short' ? v.short_id : v.codigo;
    const url = buildLink(code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(v.id + '-' + type);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success(`Link copiado: ?v=${code}`);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const inputClass = darkMode
    ? "bg-zinc-700 border border-gray-600 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
    : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange";
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? "text-gray-200" : "text-gray-700"}`;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users className={darkMode ? "text-gray-200" : "text-gray-700"} />
            <h1 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>Vendedores</h1>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-nomoz-orange hover:opacity-90 text-white font-medium"
          >
            <Plus size={16} /> Novo vendedor
          </button>
        </div>

        <div className={`rounded-lg border ${darkMode ? "border-zinc-700 bg-zinc-800" : "border-gray-200 bg-white"}`}>
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Carregando...</div>
          ) : vendedores.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={40} className="mx-auto mb-3 text-gray-400" />
              <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                Nenhum vendedor cadastrado ainda.
              </p>
              <button
                onClick={openCreate}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-nomoz-orange hover:opacity-90 text-white text-sm"
              >
                <Plus size={14} /> Cadastrar o primeiro
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className={darkMode ? "bg-zinc-700 text-gray-200" : "bg-gray-50 text-gray-700"}>
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">E-mail</th>
                  <th className="text-right px-4 py-3">Leads</th>
                  <th className="text-right px-4 py-3">Ativos</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className={darkMode ? "text-gray-200" : "text-gray-800"}>
                {vendedores.map((v) => (
                  <tr key={v.id} className={darkMode ? "border-t border-zinc-700" : "border-t border-gray-100"}>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/admin/vendedores/${v.id}`} className="hover:text-nomoz-orange hover:underline">{v.nome}</Link>
                    </td>
                    <td className="px-4 py-3 text-xs opacity-80">{v.email || "—"}</td>
                    <td className="px-4 py-3 text-right">{v.total_leads ?? 0}</td>
                    <td className="px-4 py-3 text-right">{v.clientes_ativos ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      {v.ativo ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-500">Ativo</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-500/20 text-gray-400">Inativo</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <div className="relative">
                          <button
                            onClick={() => setCopiedId(copiedId === v.id + '-menu' ? null : v.id + '-menu')}
                            className={`p-1.5 rounded hover:bg-black/10 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
                            title="Copiar link"
                          >
                            <Link2 size={16} />
                          </button>
                          {copiedId === v.id + '-menu' && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setCopiedId(null)} />
                              <div className={`absolute right-1/2 translate-x-1/2 top-full mt-2 z-20 rounded-lg shadow-xl border py-1 min-w-[170px] ${
                                darkMode ? "bg-zinc-800 border-zinc-600" : "bg-white border-gray-200"
                              }`}>
                                <button
                                  onClick={() => copyLink(v, 'short')}
                                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                                    darkMode ? "hover:bg-zinc-700 text-gray-200" : "hover:bg-gray-50 text-gray-700"
                                  }`}
                                >
                                  <Link2 size={14} className="opacity-50" />
                                  Link curto
                                </button>
                                {v.codigo && (
                                  <button
                                    onClick={() => copyLink(v, 'custom')}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                                      darkMode ? "hover:bg-zinc-700 text-gray-200" : "hover:bg-gray-50 text-gray-700"
                                    }`}
                                  >
                                    <Copy size={14} className="opacity-50" />
                                    Link personalizado
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          onClick={() => openEdit(v)}
                          className={`p-1.5 rounded hover:bg-black/10 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(v)}
                          className="p-1.5 rounded hover:bg-red-500/20 text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className={`mt-3 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          Clique no ícone <Link2 size={12} className="inline mx-0.5" /> para copiar o link de atribuição do vendedor.
          Exemplo: <span className="font-mono">{INSTITUCIONAL_URL}/?v=CODIGO</span>
        </p>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-lg shadow-xl ${darkMode ? "bg-zinc-800" : "bg-white"}`}>
            <form onSubmit={handleSubmit}>
              <div className={`px-6 py-4 border-b ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
                <h2 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
                  {editing ? "Editar vendedor" : "Novo vendedor"}
                </h2>
              </div>
              <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className={labelClass}>Nome *</label>
                  <input
                    type="text" required
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>E-mail</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Telefone</label>
                    <input
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Código (URL) <span className="opacity-60">— deixe vazio para gerar automaticamente</span>
                  </label>
                  <input
                    type="text"
                    placeholder="ex: joao-silva"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                    className={`block w-full rounded-md shadow-sm py-2 px-3 font-mono ${inputClass}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Comissão 1ª parcela (%)</label>
                    <input
                      type="number" step="0.01" min="0" max="1"
                      value={formData.comissao_pct_ciclo1}
                      onChange={(e) => setFormData({ ...formData, comissao_pct_ciclo1: parseFloat(e.target.value) || 0 })}
                      className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Ex: 0.12 = 12%
                    </p>
                  </div>
                  <div>
                    <label className={labelClass}>Comissão parcelas 2–4 (%)</label>
                    <input
                      type="number" step="0.01" min="0" max="1"
                      value={formData.comissao_pct_demais}
                      onChange={(e) => setFormData({ ...formData, comissao_pct_demais: parseFloat(e.target.value) || 0 })}
                      className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Observações</label>
                  <textarea
                    rows="2"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-nomoz-orange focus:ring-nomoz-orange"
                  />
                  <span className={`text-sm ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Ativo</span>
                </label>
              </div>
              <div className={`px-6 py-4 border-t flex justify-end gap-3 ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={`px-4 py-2 rounded-md text-sm ${darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md text-sm bg-nomoz-orange text-white hover:opacity-90"
                >
                  {editing ? "Salvar" : "Criar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminVendedores;
